import { hurryIcon, playIcon, preloadIcon } from "@loot-os/icons/animate";
import { Icon as PackIcon } from "@loot-os/icons/react";
import type { IconData, IconStyle } from "@loot-os/icons/types";
import { useEffect, useRef } from "react";

import { MOTION } from "../foundations/presets.js";
import { useThemeSpec } from "../foundations/themeContext.js";

export interface IconProps {
  /** An icon pack module's default export, e.g. `import searchIcon from "@loot-os/icons/icons/search"`. */
  icon: IconData;
  /** Width and height in px. Controls use 16; navigation and empty states 20 or 24. */
  size?: 12 | 14 | 16 | 18 | 20 | 24 | 32;
  /** Accessible name. Omit for a decorative icon beside visible text. */
  label?: string;
  className?: string;
  /**
   * Draws this style instead of the theme's, for a state the shape has to show: a toggle that is
   * off as `outline`, on as a solid style (a saved heart).
   */
  variant?: IconStyle;
  /**
   * `hover`: plays when the control the icon sits in is pointed at, tapped or keyboard-focused.
   * Only on a theme that animates every icon (`icons.motion: "all"`). For icon-only controls and
   * nav items, never an icon inside text, a list row or a field.
   */
  trigger?: "hover";
  /**
   * Plays each time this value changes after the first render: something just happened to the
   * shopper (a notification arrived, a favorite, a balance rose). On any theme whose icons
   * animate (`events` or `all`). Pass a value that changes only when the moment happens, such as
   * `useRiseCount`'s.
   */
  playOn?: unknown;
}

/** What a hover-triggered icon answers to: the nearest control around it. */
const CONTROL = "a, button, label, [role='button'], [role='menuitem'], [role='tab']";

/** Runs `task` once the browser is idle (or soon, where it can't say); returns a cancel. */
function whenIdle(task: () => void): () => void {
  if (typeof requestIdleCallback === "function") {
    const handle = requestIdleCallback(task, { timeout: 2000 });
    return () => cancelIdleCallback(handle);
  }
  const handle = setTimeout(task, 200);
  return () => clearTimeout(handle);
}

/**
 * An icon from the icon pack in `currentColor`, in the theme's style (`icons.style`): set its
 * color with a text class on it or its parent. It is still unless asked to play (`trigger`,
 * `playOn`) and the theme lets it (`icons.motion`); the theme's motion preset sets the pace, and
 * reduced motion keeps it still.
 */
export function Icon({ icon, size = 16, label, className, variant, trigger, playOn }: IconProps) {
  const spec = useThemeSpec();
  const style = variant ?? spec.icons.style;
  const motion = spec.icons.motion;
  const duration = MOTION[spec.motion.preset].iconAnimation;
  const ref = useRef<SVGSVGElement>(null);

  // An icon's animation is its own download. Where this icon can play on this theme, fetch it while
  // the page is idle, so the first play starts at once; where it can't, it is never fetched.
  const mayPlay =
    (trigger === "hover" && motion === "all") || (playOn !== undefined && motion !== "none");
  useEffect(() => {
    if (!mayPlay) return;
    return whenIdle(() => void preloadIcon(icon));
  }, [mayPlay, icon]);

  useEffect(() => {
    const svg = ref.current;
    if (trigger !== "hover" || motion !== "all" || !svg) return;
    const control = svg.closest<HTMLElement>(CONTROL) ?? svg.parentElement;
    if (!control) return;
    const play = () => playIcon(svg, icon, style, { duration });
    const onEnter = (event: PointerEvent) => {
      if (event.pointerType !== "touch") play();
    };
    const onDown = (event: PointerEvent) => {
      if (event.pointerType === "touch") play();
    };
    // A lifted finger also leaves; only a mouse or pen moving away hurries the run to its end.
    const onLeave = (event: PointerEvent) => {
      if (event.pointerType !== "touch") hurryIcon(svg);
    };
    const onFocus = () => {
      if (control.matches(":focus-visible")) play();
    };
    control.addEventListener("pointerenter", onEnter);
    control.addEventListener("pointerdown", onDown);
    control.addEventListener("pointerleave", onLeave);
    control.addEventListener("focus", onFocus);
    return () => {
      control.removeEventListener("pointerenter", onEnter);
      control.removeEventListener("pointerdown", onDown);
      control.removeEventListener("pointerleave", onLeave);
      control.removeEventListener("focus", onFocus);
    };
  }, [trigger, motion, icon, style, duration]);

  const lastPlayOn = useRef(playOn);
  useEffect(() => {
    if (Object.is(lastPlayOn.current, playOn)) return;
    lastPlayOn.current = playOn;
    const svg = ref.current;
    if (svg && motion !== "none") playIcon(svg, icon, style, { duration });
  }, [playOn, motion, icon, style, duration]);

  return (
    <PackIcon
      ref={ref}
      icon={icon}
      variant={style}
      size={size}
      animated={false}
      label={label}
      className={className}
    />
  );
}
